import { useContext, useEffect, useReducer, useState } from 'react';
import { createPortal } from 'react-dom';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { Box, Button, Paper, Stack, TextField } from '@mui/material';

import { CanvasContext } from '@/designs-dev/handover/Canvas';
import { useInspect } from '@/designs-dev/inspect/InspectContext';
import { anchorFor, resolveAnchors } from '@/designs-dev/comments/anchor';
import { ThreadBubble } from '@/designs-dev/comments/Thread';
import {
  addComment,
  commentsSnapshot,
  contentOf,
  isResolved,
  isUnread,
  selectThread,
  startDraft,
  threadsOf,
  useComments,
  watchPage,
} from '@/designs-dev/comments/store';

// The comment layer lives where InspectOverlay does, inside the screen, so
// every stage that has drill mode gets comments too. In comment mode a click
// on the screen starts a comment there instead of doing what it would do, and
// each thread shows as a numbered pin.

// Which page a comment belongs to: the path, plus the scene, since one path can
// show several states of a screen.
export const usePageKey = () => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const scene = searchParams.get('scene');
  return scene ? `${pathname}?scene=${scene}` : pathname;
};

const PIN = 26;

export const Pin = ({
  id,
  n,
  selected,
  onClick,
  top,
  left,
  zoom = 1,
  unread = false,
}: {
  // The thread's id, for its bubble to find it by.
  id?: string;
  n: number | null;
  selected: boolean;
  onClick?: () => void;
  top: number;
  left: number;
  // The zoom of whatever the pin sits in. A canvas scales its pins with the
  // page; this undoes that so they stay the same size at any zoom.
  zoom?: number;
  // Someone else has said something here since I last opened it.
  unread?: boolean;
}) => (
  <Box
    data-drill-handle
    data-comment-pin={id}
    onClick={(e) => {
      e.stopPropagation();
      onClick?.();
    }}
    sx={{
      // The crosshair rule in styles.css gives way to handles; this says what
      // a click on a pin does.
      '--drill-cursor': 'pointer',
      position: 'absolute',
      top: top - PIN,
      left,
      width: PIN,
      height: PIN,
      zIndex: 40,
      // For the unread dot, which hangs off the corner.
      overflow: 'visible',
      transform: zoom === 1 ? undefined : `scale(${1 / zoom})`,
      transformOrigin: '0 100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      // A speech-bubble pin: round except the corner that points at the spot.
      borderRadius: `${PIN / 2}px ${PIN / 2}px ${PIN / 2}px 2px`,
      bgcolor: selected ? 'secondary.main' : 'primary.main',
      color: 'common.white',
      border: '2px solid',
      borderColor: 'common.white',
      typography: 'caption',
      fontWeight: 600,
    }}
  >
    {n}
    {unread && (
      <Box
        sx={{
          position: 'absolute',
          top: -4,
          right: -4,
          width: 10,
          height: 10,
          borderRadius: '50%',
          bgcolor: 'custom.coral.600',
          border: '2px solid',
          borderColor: 'common.white',
        }}
      />
    )}
  </Box>
);

export const Composer = ({
  left,
  top,
  onCancel,
  onSubmit,
}: {
  left: number;
  top: number;
  onCancel: () => void;
  onSubmit: (text: string) => Promise<void>;
}) => {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const send = () => {
    if (!text.trim() || sending) return;
    setSending(true);
    setFailed(false);
    onSubmit(text.trim()).catch((error: unknown) => {
      console.error('posting comment failed', error);
      setFailed(true);
      setSending(false);
    });
  };
  return createPortal(
    <Paper
      onClick={(e) => e.stopPropagation()}
      sx={{
        position: 'fixed',
        // Beside the pin, kept on screen.
        left: Math.min(left + 12, window.innerWidth - 300),
        top: Math.min(top, window.innerHeight - 180),
        width: 280,
        p: 1.5,
        zIndex: 1400,
        border: 1,
        borderColor: 'custom.neutral.200',
      }}
    >
      <Stack spacing={1}>
        <TextField
          autoFocus
          multiline
          minRows={2}
          size='small'
          placeholder='Add a comment'
          value={text}
          error={failed}
          helperText={failed ? "Couldn't post that. Try again." : undefined}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
            if (e.key === 'Escape') onCancel();
          }}
        />
        <Stack direction='row' spacing={2} sx={{ justifyContent: 'flex-end' }}>
          <Button size='small' variant='text' onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size='small'
            variant='contained'
            disabled={!text.trim() || sending}
            onClick={send}
          >
            Comment
          </Button>
        </Stack>
      </Stack>
    </Paper>,
    document.body,
  );
};

const CommentLayer = ({ screenEl }: { screenEl: HTMLElement | null }) => {
  const { mode, setMode, setCurrent, setHovered } = useInspect();
  const commenting = mode === 'comment';
  const page = usePageKey();

  // C toggles comment mode, as in Figma. Listened for whatever the mode, since
  // it is also the way in. Not inside a canvas, where C belongs to the page's
  // own comments (CanvasComments).
  const inCanvas = useContext(CanvasContext) !== null;
  useEffect(() => {
    if (!screenEl || inCanvas) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'c' || e.metaKey || e.ctrlKey || e.altKey)
        return;
      // Don't hijack the key while typing, on the prototype or in a comment.
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        el?.isContentEditable
      )
        return;
      e.preventDefault();
      setCurrent(null);
      setHovered(null);
      setMode(commenting ? 'view' : 'comment');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [screenEl, inCanvas, commenting, setMode, setCurrent, setHovered]);
  const store = useComments();
  const { comments, selected, draft } = store;
  const { user } = useKindeAuth();
  // Where the click that started the draft was, in window space, for the
  // composer (which sits outside the screen so its text can be selected).
  const [draftAt, setDraftAt] = useState<{ x: number; y: number } | null>(null);
  // Pins are measured from the DOM, so anything that moves the screen's
  // contents re-renders them.
  const [, remeasure] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    // Loaded whatever the mode, so the bar can say how many there are before
    // comment mode is on.
    return watchPage(page);
  }, [page]);

  // Leaving comment mode puts away whatever was open in it.
  useEffect(() => {
    if (commenting) return;
    startDraft(null);
    selectThread(null);
  }, [commenting]);

  useEffect(() => {
    if (!commenting || !screenEl) return;

    const onClick = (e: MouseEvent) => {
      // Pins handle their own clicks.
      if ((e.target as HTMLElement).closest('[data-drill-handle]')) return;
      e.preventDefault();
      e.stopPropagation();
      const { anchor, el } = anchorFor(screenEl, e.target as HTMLElement);
      const r = el.getBoundingClientRect();
      startDraft({
        anchor,
        x: r.width ? (e.clientX - r.left) / r.width : 0,
        y: r.height ? (e.clientY - r.top) / r.height : 0,
      });
      setDraftAt({ x: e.clientX, y: e.clientY });
    };
    // Esc backs out a step at a time: the draft, the open thread, then the mode.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const { draft: d, selected: s } = commentsSnapshot();
      if (d) startDraft(null);
      else if (s) selectThread(null);
      else setMode('view');
    };

    // Reuses drill mode's flag: a crosshair everywhere and no press effects,
    // so the screen reads as something you point at rather than use.
    screenEl.dataset.drill = 'on';
    screenEl.dataset.comment = 'on';
    screenEl.addEventListener('click', onClick, true);
    window.addEventListener('keydown', onKey);
    const resize = new ResizeObserver(remeasure);
    resize.observe(screenEl);
    screenEl.addEventListener('scroll', remeasure, true);
    return () => {
      delete screenEl.dataset.drill;
      delete screenEl.dataset.comment;
      screenEl.removeEventListener('click', onClick, true);
      window.removeEventListener('keydown', onKey);
      resize.disconnect();
      screenEl.removeEventListener('scroll', remeasure, true);
    };
  }, [commenting, screenEl, setMode]);

  if (!commenting || !screenEl) return null;

  // The same scale-aware mapping as the drill overlay: the screen may be
  // CSS-scaled, and the portal's space is the screen's own.
  const s = screenEl.getBoundingClientRect();
  const scale = screenEl.offsetWidth ? s.width / screenEl.offsetWidth : 1;
  const threads = threadsOf(comments);
  // A resolved thread's pin is put away, unless it's the one open: opening it
  // from the list still needs somewhere to hang its bubble.
  const anchors = resolveAnchors(screenEl, [
    ...threads.map((t) => t.anchor),
    ...(draft ? [draft.anchor] : []),
  ]);
  const pointOn = (anchor: string, x = 0, y = 0) => {
    const el = anchors.get(anchor);
    if (!el || !screenEl.contains(el)) return null;
    const r = el.getBoundingClientRect();
    return {
      left: (r.left - s.left + x * r.width) / scale,
      top: (r.top - s.top + y * r.height) / scale,
    };
  };
  const draftPin = draft && pointOn(draft.anchor, draft.x, draft.y);

  return (
    <>
      {createPortal(
        <>
          {threads.map((t, i) => {
            const { x, y } = contentOf(t);
            if (isResolved(comments, t.id) && selected !== t.id) return null;
            const at = pointOn(t.anchor, x, y);
            if (!at) return null;
            return (
              <Pin
                key={t.id}
                n={i + 1}
                selected={selected === t.id}
                id={t.id}
                unread={isUnread(store, t, user?.id)}
                onClick={() => selectThread(selected === t.id ? null : t.id)}
                {...at}
              />
            );
          })}
          {draftPin && <Pin n={null} selected {...draftPin} />}
        </>,
        screenEl,
      )}
      {draft && draftAt && (
        <Composer
          left={draftAt.x}
          top={draftAt.y}
          onCancel={() => startDraft(null)}
          onSubmit={async (text) => {
            const created = await addComment(draft.anchor, {
              text,
              x: draft.x,
              y: draft.y,
            });
            if (created) selectThread(created.id);
          }}
        />
      )}
      <ThreadBubble />
    </>
  );
};

export default CommentLayer;
